export interface LocalDevice {
  serial?: string | null;
  model?: number | string | null;
  [key: string]: any;
}

export type DeviceNamesMap = Record<string, LocalDevice>;

/**
 * Construye la etiqueta "oficial" de Marsinet para un dispositivo local
 * (ej: "AT-2501001" o "EXP-2501001" según el modelo).
 */
export const buildOurDeviceLabel = (
  device: LocalDevice | undefined | null
): string => {
  if (!device) return 'N/A';

  const serial = String(device.serial || '').trim();
  if (!serial) return 'N/A';

  const prefix = Number(device.model) === 5 ? 'AT' : 'EXP';

  return `${prefix}-${serial}`;
};

/**
 * Resuelve qué texto mostrar para el dispositivo de una fila (incidencia u OM),
 * priorizando siempre el nombre local de Marsinet sobre el dato crudo de RPS.
 *
 * Orden de resolución:
 *  1. Match exacto por device_id contra el mapa local.
 *  2. Match por serial_number (limpiando prefijo AT-/EXP-) contra el mapa local.
 *  3. Fallback: serial de RPS formateado con prefijo normalizado.
 *  4. 'N/A' si no hay ningún dato.
 */
export const getDeviceDisplayValue = (
  rowData: any,
  deviceNames: DeviceNamesMap
): string => {
  const localDeviceById = deviceNames[String(rowData?.device_id || '')];

  if (localDeviceById) return buildOurDeviceLabel(localDeviceById);

  const rpsSerial = String(rowData?.serial_number || '').trim();

  if (rpsSerial.length > 0) {
    const cleanSerial = rpsSerial
      .replace(/^(AT|EXP)-?/i, '')
      .trim()
      .toLowerCase();

    const localDeviceBySerial = Object.values(deviceNames).find(
      (device) =>
        String(device?.serial || '')
          .trim()
          .toLowerCase() === cleanSerial
    );

    if (localDeviceBySerial) return buildOurDeviceLabel(localDeviceBySerial);

    if (/^(AT|EXP)-/i.test(rpsSerial)) {
      const normalizedPrefix = rpsSerial.toUpperCase().startsWith('AT')
        ? 'AT'
        : 'EXP';

      return `${normalizedPrefix}-${cleanSerial.toUpperCase()}`;
    }

    return rpsSerial;
  }

  return 'N/A';
};
